// Car dynamics: a single-track ("bicycle") model with simplified Pacejka tyres,
// friction-ellipse combined slip, longitudinal load transfer, active aero and
// the 2026 power unit. The hidden assists (speed-sensitive steering, traction
// control, ABS-style braking, stability) are part of making tilt input forgiving.
//
// Sim mode (steering wheel + pedals) drops those assists: the wheel angle is
// direct, brakes apply pedal force and can lock the wheels, the rear can spin
// up under power, the gearbox runs an engine model with a clutch, and the
// front tyres' self-aligning torque is computed for force feedback.
//
// Body frame: x forward, y right; yaw rate r positive = turning right.
// World: x east, z north; heading 0 = +z, positive towards +x.

import { param } from '../config/params';
import { ActiveAero, AIR_DENSITY, aeroParams } from './aero';
import { PowerUnit } from './powerUnit';
import { Gearbox, TYRE_RADIUS } from './gearbox';

const G = 9.81;
const DEG = Math.PI / 180;

export const carParams = {
  // C4.1: 724 kg + nominal tyre mass (~46 kg), plus a light qualifying fuel load.
  mass: param({ id: 'car.mass', label: 'Mass (kg)', group: 'Car', min: 700, max: 900, step: 5, value: 790 }),
  wheelbase: param({ id: 'car.wheelbase', label: 'Wheelbase (m)', group: 'Car', min: 3.0, max: 3.6, step: 0.01, value: 3.4 }),
  frontWeight: param({ id: 'car.frontWeight', label: 'Static front weight', group: 'Car', min: 0.4, max: 0.5, step: 0.005, value: 0.45 }),
  cgHeight: param({ id: 'car.cgHeight', label: 'CG height (m)', group: 'Car', min: 0.2, max: 0.4, step: 0.01, value: 0.3 }),
  yawInertia: param({ id: 'car.yawInertia', label: 'Yaw inertia (kg·m²)', group: 'Car', min: 600, max: 2000, step: 10, value: 1100 }),
  tyreMu: param({ id: 'tyre.mu', label: 'Tyre peak μ', group: 'Tyres', min: 0.8, max: 2.2, step: 0.01, value: 1.56 }),
  tyreB: param({ id: 'tyre.B', label: 'Tyre stiffness B', group: 'Tyres', min: 4, max: 30, step: 0.5, value: 18 }),
  tyreC: param({ id: 'tyre.C', label: 'Tyre shape C', group: 'Tyres', min: 1.1, max: 2.0, step: 0.05, value: 1.4 }),
  brakeBias: param({ id: 'car.brakeBias', label: 'Brake bias front', group: 'Car', min: 0.45, max: 0.7, step: 0.01, value: 0.56 }),
  drivetrainEff: param({ id: 'car.drivetrainEff', label: 'Drivetrain efficiency', group: 'Car', min: 0.8, max: 1, step: 0.01, value: 0.93 }),
  rollingRes: param({ id: 'car.rollingRes', label: 'Rolling resistance', group: 'Car', min: 0, max: 0.05, step: 0.001, value: 0.015 }),
  steerLowDeg: param({ id: 'assist.steerLowDeg', label: 'Max wheel angle slow (deg)', group: 'Assists', min: 5, max: 30, step: 0.5, value: 18 }),
  steerGripMargin: param({ id: 'assist.steerGripMargin', label: 'Full lock vs grip limit', group: 'Assists', min: 0.8, max: 1.6, step: 0.05, value: 1.0 }),
  steerRate: param({ id: 'assist.steerRate', label: 'Wheel slew rate (rad/s)', group: 'Assists', min: 0.5, max: 10, step: 0.1, value: 3 }),
  stability: param({ id: 'assist.stability', label: 'Stability assist', group: 'Assists', min: 0, max: 20, step: 0.5, value: 8 }),
  slipAssist: param({ id: 'assist.slip', label: 'Body-slip assist', group: 'Assists', min: 0, max: 80, step: 1, value: 30 }),
  slipAssistDeg: param({ id: 'assist.slipDeg', label: 'Body-slip assist from (deg)', group: 'Assists', min: 1, max: 15, step: 0.5, value: 4 }),
  // Sim mode (wheel). F1 steering is direct: ~±18° at the front wheels for about ±180° at the rim.
  steerLockDeg: param({ id: 'car.steerLockDeg', label: 'Sim: front wheel angle at full lock (deg)', group: 'Sim', min: 10, max: 30, step: 0.5, value: 18 }),
  // Enough to lock the wheels at all but the highest speeds (where downforce adds grip): trail the brake off as you slow.
  brakeMaxG: param({ id: 'car.brakeMaxG', label: 'Sim: max brake force (x weight)', group: 'Sim', min: 2, max: 8, step: 0.1, value: 5 }),
  slideMu: param({ id: 'tyre.slideMu', label: 'Sim: sliding grip (x peak)', group: 'Sim', min: 0.5, max: 1, step: 0.01, value: 0.8 }),
  // Brush model: contact length / 6 ≈ 15–37 mm over the F1 front load range.
  pneumaticTrail: param({ id: 'tyre.pneumaticTrail', label: 'Sim: pneumatic trail at zero slip (m)', group: 'Sim', min: 0, max: 0.1, step: 0.005, value: 0.03 }),
  trailFalloff: param({ id: 'tyre.trailFalloff', label: 'Sim: trail gone at (x peak slip)', group: 'Sim', min: 1, max: 3, step: 0.05, value: 1.3 }),
  casterTrail: param({ id: 'car.casterTrail', label: 'Sim: mechanical (caster) trail (m)', group: 'Sim', min: 0, max: 0.06, step: 0.005, value: 0.02 }),
  // Rear tyres are 375 mm wide against 280 mm at the front: more cornering stiffness and
  // grip per unit load. Without this the axles are exactly balanced and the car spins on a lift.
  rearStiffness: param({ id: 'tyre.rearStiffness', label: 'Sim: rear cornering stiffness (x front)', group: 'Sim', min: 0.8, max: 2, step: 0.05, value: 1.3 }),
  rearGrip: param({ id: 'tyre.rearGrip', label: 'Sim: rear grip (x front)', group: 'Sim', min: 0.9, max: 1.2, step: 0.01, value: 1.1 }),
  // Tyre relaxation length: lateral force builds over this much rolling distance.
  relaxFront: param({ id: 'tyre.relaxFront', label: 'Sim: relaxation length front (m)', group: 'Sim', min: 0.05, max: 1, step: 0.05, value: 0.4 }),
  relaxRear: param({ id: 'tyre.relaxRear', label: 'Sim: relaxation length rear (m)', group: 'Sim', min: 0.05, max: 1, step: 0.05, value: 0.45 }),
  // Yaw inertia for sim mode. Estimate: published F1 single-track models use ~1200 kg·m² for
  // ~660 kg cars at this wheelbase; scaled to 790 kg that's ~1400. The tilt value (1100) is on
  // the twitchy side, which the phone assists hide; without them it makes slides snap.
  yawInertiaSim: param({ id: 'car.yawInertiaSim', label: 'Sim: yaw inertia (kg·m²)', group: 'Sim', min: 600, max: 2000, step: 10, value: 1300 }),
  // First-order stand-in for the stiff (~3–4 Hz) pitch mode.
  pitchLag: param({ id: 'car.pitchLag', label: 'Sim: load transfer lag (s)', group: 'Sim', min: 0, max: 0.3, step: 0.01, value: 0.08 }),
  // Tyre load sensitivity: μ falls as load rises, μ(Fz) = μ0·(1 − k·(Fz/Fz0 − 1)), with Fz0 a
  // wheel's load at ~200 km/h (so mid-speed grip is unchanged). Together with lateral load
  // transfer it makes the axle that takes more of the transfer lose grip: the front roll
  // share is the balance knob (more front = more understeer). It also blunts the balance
  // shift from longitudinal transfer, which is what keeps a trail-braked turn-in from
  // snapping the rear (k ≈ 0.1 is typical of racing slicks). Costs ~10% of the grip at top
  // speed; the sim-mode quasi-steady lap (physics:qss SIM=1) is 1:35.8 against a 1:35.1 pole.
  loadSensitivity: param({ id: 'tyre.loadSensitivity', label: 'Sim: tyre load sensitivity', group: 'Sim', min: 0, max: 0.3, step: 0.01, value: 0.1 }),
  rollShareFront: param({ id: 'car.rollShareFront', label: 'Sim: front roll stiffness share', group: 'Sim', min: 0.3, max: 0.7, step: 0.01, value: 0.55 }),
  engineBrakeNm: param({ id: 'pu.engineBrakeNm', label: 'Sim: engine braking at the crank (N·m)', group: 'Sim', min: 0, max: 150, step: 5, value: 60 }),
  // F1 engine-braking maps limit off-throttle rear torque (harvest + engine friction) to keep
  // the rear stable on a lift: as a share of the rear axle's grip.
  // Brake-by-wire: the rear brake torque is computed, not plumbed, and F1 cars run
  // "brake migration" so the balance moves forward as speed (and downforce) drops
  // and weight shifts to the front. Keeps the fronts locking before the rears.
  brakeMigration: param({ id: 'car.brakeMigration', label: 'Sim: brake migration at low speed (+front)', group: 'Sim', min: 0, max: 0.1, step: 0.005, value: 0.04 }),
  brakeByWireOffset: param({ id: 'car.bbwOffset', label: 'Sim: brake-by-wire front offset', group: 'Sim', min: 0, max: 0.06, step: 0.005, value: 0.02 }),
  engineBrakeMap: param({ id: 'pu.engineBrakeMap', label: 'Sim: engine braking map (x rear grip)', group: 'Sim', min: 0, max: 1, step: 0.05, value: 0.1 }),
  // F1 pedal maps are torque-demand maps, shaped for driveability: torque ∝ pedal^x gives
  // finer control at part throttle, where the rears are on the edge of spinning.
  throttleMap: param({ id: 'pu.throttleMap', label: 'Sim: throttle map progressivity (torque ∝ pedal^x)', group: 'Sim', min: 1, max: 2.5, step: 0.05, value: 1.7 }),
  // TC low: how far past the cornering-aware limit (TC high's) it lets the torque go, so the
  // rear can step out under power, and the wheelspin it allows (share of a full spin).
  tcLowSpare: param({ id: 'assist.tcLowSpare', label: 'Sim: TC low torque allowance (x TC high)', group: 'Sim', min: 1, max: 2, step: 0.05, value: 1.5 }),
  tcLowSlip: param({ id: 'assist.tcLowSlip', label: 'Sim: TC low wheelspin allowed', group: 'Sim', min: 0, max: 1, step: 0.05, value: 0.5 }),
  stabilityAid: param({ id: 'assist.stabilitySim', label: 'Sim: stability aid strength', group: 'Sim', min: 0, max: 10, step: 0.5, value: 3 }),
};

/**
 * Sim mode driving aids. Real F1 has none of them (TC and ABS are banned and
 * the standard ECU can't run them), so all off is the realistic setting.
 * - tc: 0 off; 1 low: lets the rears spin briefly, then cuts torque (the rear
 *   moves, but a floored exit doesn't become a spin); 2 high: holds the rears
 *   at the grip limit, no wheelspin.
 * - abs: brakes never ask for more than the tyres' spare grip, so no lock-ups.
 * - stability: a mild yaw damper once the rear slides past its peak slip.
 */
export interface SimAids {
  tc: 0 | 1 | 2;
  abs: boolean;
  stability: boolean;
}
export const NO_AIDS: SimAids = { tc: 0, abs: false, stability: false };
/** The old on/off "aids" toggle: traction control and anti-lock brakes. */
export const FULL_AIDS: SimAids = { tc: 2, abs: true, stability: false };

export function sameAids(a: SimAids, b: SimAids): boolean {
  return a.tc === b.tc && a.abs === b.abs && a.stability === b.stability;
}

/** Lock-up / wheelspin onset and recovery time constant, s. */
const LOCK_TAU = 0.03;
/** TC low: how fast it pulls the rear slip back to its target, s. */
const TC_LOW_TAU = 0.05;
/** Inertia of the two rear wheels, and of the engine/MGU-K at the crank, kg·m² (estimates). */
const REAR_WHEELS_INERTIA = 2.4;
const ENGINE_INERTIA = 0.1;
/**
 * Wheel-centre track widths, m: 1.9 m maximum width (C2.3.1) less half of each
 * 280 mm front / 375 mm rear tyre.
 */
const TRACK_FRONT = 1.62;
const TRACK_REAR = 1.53;
/** Reference speed for the load-sensitivity nominal load, m/s (~200 km/h). */
const LOAD_REF_SPEED = 55;

/**
 * Grip of an axle's two tyres, N, with lateral load transfer `dFz` from the
 * inside to the outside wheel and load sensitivity `k` about `fz0` per wheel.
 */
export function axleGrip(mu: number, fz: number, dFz: number, fz0: number, k: number): number {
  const transfer = Math.min(Math.abs(dFz), fz / 2);
  const wheel = (load: number): number => mu * Math.max(0.3, 1 - k * (load / fz0 - 1)) * load;
  return wheel(fz / 2 - transfer) + wheel(fz / 2 + transfer);
}

/**
 * Sim mode steady-state cornering limit at `speed` (m/s) with downforce area
 * `clA`: the largest lateral force (N) both axles can carry in their static
 * shares (front b/L, rear a/L), with rear grip, lateral load transfer and load
 * sensitivity as in Car.step. For lap-time estimates (physics:qss SIM=1).
 */
export function simCorneringGrip(speed: number, clA: number): number {
  const m = carParams.mass.value;
  const L = carParams.wheelbase.value;
  const b = L * carParams.frontWeight.value;
  const a = L - b;
  const bal = aeroParams.balanceFront.value;
  const df = 0.5 * AIR_DENSITY * clA * speed * speed;
  const fzF = (m * G * b) / L + df * bal;
  const fzR = (m * G * a) / L + df * (1 - bal);
  const qRef = 0.5 * AIR_DENSITY * LOAD_REF_SPEED * LOAD_REF_SPEED * aeroParams.clACorner.value;
  const fz0F = ((m * G * b) / L + qRef * bal) / 2;
  const fz0R = ((m * G * a) / L + qRef * (1 - bal)) / 2;
  const k = carParams.loadSensitivity.value;
  const mu = carParams.tyreMu.value;
  const share = carParams.rollShareFront.value;
  const h = carParams.cgHeight.value;
  const ok = (ay: number): boolean => {
    const lat = m * ay * h;
    const capF = axleGrip(mu, fzF, (lat * share) / TRACK_FRONT, fz0F, k);
    const capR = axleGrip(mu * carParams.rearGrip.value, fzR, (lat * (1 - share)) / TRACK_REAR, fz0R, k);
    return capF >= (m * ay * b) / L && capR >= (m * ay * a) / L;
  };
  let lo = 0;
  let hi = 80;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (ok(mid)) lo = mid;
    else hi = mid;
  }
  return m * lo;
}

export interface CarInput {
  steer: number; // -1..1, positive = right
  throttle: number; // 0..1
  brake: number; // 0..1
  /** Clutch pedal 0 (released) .. 1 (pressed); sim mode H-pattern only. */
  clutch?: number;
}

export interface CarEnv {
  /** Grip multiplier of the surface under the car (1 = dry track). */
  grip: number;
  /** Extra resistance of the surface as a fraction of weight (grass, gravel). */
  drag: number;
  /** Inside an active-aero activation zone. */
  aeroZone: boolean;
  overtake: boolean;
  /** Road gradient along the car's heading (rise over run; positive = uphill). */
  grade?: number;
  /** Cap on MGU-K deployment here, kW (race sector limits, wet map). */
  deployCapKW?: number;
  /** Only the front wing may open in Straight Mode (Low Grip Conditions, B7.1.2b). */
  partialAero?: boolean;
  /** Sim mode: right-side minus left-side surface drag (fractions of weight); yaws the car towards the draggier side. */
  sideDrag?: number;
}

/**
 * Race-state modifiers, set by the session each step: fuel load, tyre grip per
 * axle (compound, wear, wet track), aero damage and setup. Neutral by default.
 */
export interface CarMods {
  /** Mass on top of carParams.mass (fuel), kg. */
  extraMass: number;
  /** Grip multipliers on top of the surface grip. */
  gripFront: number;
  gripRear: number;
  /** Downforce and drag multipliers (damage, setup). */
  downforce: number;
  drag: number;
  /** Added to the front aero balance (front wing damage moves it rearwards). */
  balanceShift: number;
  /** Brake bias override (setup); undefined uses carParams.brakeBias. */
  brakeBias?: number;
}

export function neutralMods(): CarMods {
  return { extraMass: 0, gripFront: 1, gripRear: 1, downforce: 1, drag: 1, balanceShift: 0 };
}

export const DRY_TRACK: CarEnv = { grip: 1, drag: 0, aeroZone: false, overtake: false };

export class Car {
  x = 0;
  z = 0;
  heading = 0;
  vx = 0;
  vy = 0;
  yawRate = 0;
  wheelAngle = 0;
  /** Body-frame longitudinal acceleration from the last step, m/s². */
  longAccel = 0;
  latAccel = 0;

  readonly aero = new ActiveAero();
  readonly pu = new PowerUnit();
  readonly gearbox = new Gearbox();
  mods: CarMods = neutralMods();

  // Telemetry from the last step.
  slipFront = 0;
  slipRear = 0;
  gripUseFront = 0;
  gripUseRear = 0;
  /** MGU-K power, W: deploying (+) or harvesting (−). */
  mguK = 0;

  /** Wheel mode: direct steering, real brakes and traction, engine model, no hidden assists. */
  simMode = false;
  /** Sim mode aids (traction control, anti-lock brakes, stability). */
  aids: SimAids = NO_AIDS;
  /** Sim mode rear wheelspin: wheel surface speed minus road speed, m/s. */
  private rearSlip = 0;
  /** 0..1 how locked the front / rear wheels are, and how much the rears are spinning (sim mode). */
  frontLock = 0;
  rearLock = 0;
  rearSpin = 0;
  /** 0..1 share of the brake request anti-lock is holding back, and of the drive torque traction control is cutting (either mode). */
  absCut = 0;
  tcCut = 0;
  /** Self-aligning torque of the front tyres about the steering axes, N·m (+ turns the wheel right). */
  aligningTorque = 0;
  /** Sim mode transient slip angles (relaxation length), rad. */
  private alphaF = 0;
  private alphaR = 0;
  /** Sim mode lagged longitudinal acceleration for the load transfer. */
  private axLagged = 0;

  /** `aids`: true is the old on/off toggle (TC high + ABS). */
  setSimMode(on: boolean, aids: SimAids | boolean = NO_AIDS): void {
    this.simMode = on;
    this.aids = aids === true ? FULL_AIDS : aids === false ? NO_AIDS : { ...aids };
    this.gearbox.engineModel = on;
    if (!on) this.gearbox.setMode('auto', this.vx);
  }

  get speed(): number {
    return Math.hypot(this.vx, this.vy);
  }

  place(x: number, z: number, heading: number, speed: number): void {
    this.x = x;
    this.z = z;
    this.heading = heading;
    this.vx = speed;
    this.vy = 0;
    this.yawRate = 0;
    this.wheelAngle = 0;
    this.longAccel = 0;
    this.latAccel = 0;
    this.frontLock = this.rearLock = this.rearSpin = 0;
    this.absCut = this.tcCut = 0;
    this.aligningTorque = 0;
    this.alphaF = this.alphaR = 0;
    this.axLagged = 0;
    this.rearSlip = 0;
  }

  /**
   * Maximum front wheel angle for the current speed (steering assist). Full
   * lock is the angle that just uses all the front grip: the geometric angle
   * for the tightest radius the grip allows, plus the tyre's peak slip angle.
   * So full tilt means "corner at the limit" at any speed, and the car never
   * runs wide just because the assist ran out of lock.
   */
  maxWheelAngle(): number {
    const low = carParams.steerLowDeg.value * DEG;
    const v = Math.max(this.vx, 1);
    const downforce = 0.5 * AIR_DENSITY * this.aero.clA * this.mods.downforce * v * v;
    const aLat = carParams.tyreMu.value * this.mods.gripFront * (G + downforce / (carParams.mass.value + this.mods.extraMass));
    const geometric = (carParams.wheelbase.value * aLat) / (v * v);
    const peakSlip = Math.tan(Math.PI / (2 * carParams.tyreC.value)) / carParams.tyreB.value;
    return Math.min(low, (geometric + peakSlip) * carParams.steerGripMargin.value);
  }

  step(input: CarInput, env: CarEnv, dt: number): void {
    const mods = this.mods;
    const m = carParams.mass.value + mods.extraMass;
    const L = carParams.wheelbase.value;
    const b = L * carParams.frontWeight.value; // CG to rear axle
    const a = L - b; // CG to front axle
    const h = carParams.cgHeight.value;
    const Iz = this.simMode ? carParams.yawInertiaSim.value : carParams.yawInertia.value;
    const B = carParams.tyreB.value;
    const C = carParams.tyreC.value;

    const sim = this.simMode;
    const gb = this.gearbox;
    if (sim) {
      // Wheel: the rim is the steering, 1:1 through the rack.
      this.wheelAngle = clamp(input.steer, -1, 1) * carParams.steerLockDeg.value * DEG;
    } else {
      // Tilt: speed-sensitive lock, rate-limited wheel.
      const targetAngle = clamp(input.steer, -1, 1) * this.maxWheelAngle();
      const maxSlew = carParams.steerRate.value * dt;
      this.wheelAngle += clamp(targetAngle - this.wheelAngle, -maxSlew, maxSlew);
    }
    const delta = this.wheelAngle;
    if (sim && (this.vx < 0 || (gb.gear < 0 && this.vx < 1))) {
      this.stepReverse(input, env, delta, dt);
      return;
    }

    this.aero.update(env.aeroZone, input.throttle, input.brake, dt, env.partialAero);
    const kDynBefore = kDynFor(this.vx);

    const v2 = this.vx * this.vx + this.vy * this.vy;
    const q = 0.5 * AIR_DENSITY * v2;
    const downforce = q * this.aero.clA * mods.downforce;
    const dragForce = q * this.aero.cdA * mods.drag;
    const balance = aeroParams.balanceFront.value + mods.balanceShift;

    const transfer = (m * (sim ? this.axLagged : this.longAccel) * h) / L;
    const fzF = Math.max(0, (m * G * b) / L + downforce * balance - transfer);
    const fzR = Math.max(0, (m * G * a) / L + downforce * (1 - balance) + transfer);
    const mu = carParams.tyreMu.value * env.grip;
    let dF = mu * mods.gripFront * fzF;
    let dR = mu * mods.gripRear * fzR * (sim ? carParams.rearGrip.value : 1);
    if (sim) {
      // Lateral load transfer (from the last step's lateral acceleration) and load sensitivity.
      const k = carParams.loadSensitivity.value;
      const qRef = 0.5 * AIR_DENSITY * LOAD_REF_SPEED * LOAD_REF_SPEED * aeroParams.clACorner.value;
      const fz0F = ((m * G * b) / L + qRef * aeroParams.balanceFront.value) / 2;
      const fz0R = ((m * G * a) / L + qRef * (1 - aeroParams.balanceFront.value)) / 2;
      const latTransfer = m * this.latAccel * h;
      const share = carParams.rollShareFront.value;
      dF = axleGrip(mu * mods.gripFront, fzF, (latTransfer * share) / TRACK_FRONT, fz0F, k);
      dR = axleGrip(mu * mods.gripRear * carParams.rearGrip.value, fzR, (latTransfer * (1 - share)) / TRACK_REAR, fz0R, k);
    }
    const BR = sim ? B * carParams.rearStiffness.value : B;

    // Lateral forces first, from the full grip available.
    const vxs = Math.max(this.vx, 0.5);
    let alphaF: number;
    let alphaR: number;
    if (sim) {
      // Transient slip (Pacejka, Tyre & Vehicle Dynamics ch. 7): σ·dα/dt = −v_lat − |v_long|·α,
      // implicit Euler. Removes step-to-step chatter and the 1/v blow-up at low speed.
      const vyF = this.vy + a * this.yawRate;
      const vLatF = -this.vx * Math.sin(delta) + vyF * Math.cos(delta);
      const vLongF = this.vx * Math.cos(delta) + vyF * Math.sin(delta);
      const vLatR = this.vy - b * this.yawRate;
      const kF = dt / carParams.relaxFront.value;
      const kR = dt / carParams.relaxRear.value;
      this.alphaF = clamp((this.alphaF - kF * vLatF) / (1 + kF * Math.abs(vLongF)), -1.2, 1.2);
      this.alphaR = clamp((this.alphaR - kR * vLatR) / (1 + kR * Math.abs(this.vx)), -1.2, 1.2);
      alphaF = this.alphaF;
      alphaR = this.alphaR;
    } else {
      alphaF = delta - Math.atan2(this.vy + a * this.yawRate, vxs);
      alphaR = -Math.atan2(this.vy - b * this.yawRate, vxs);
    }
    let fyF = dF * Math.sin(C * Math.atan(B * alphaF));
    let fyR = dR * Math.sin(C * Math.atan(BR * alphaR));

    // Assists (ABS / traction control) only use the grip left over from cornering,
    // so braking or accelerating mid-corner never makes the car spin out.
    const spareF = 0.95 * Math.sqrt(Math.max(0, dF * dF - fyF * fyF));
    const spareR = 0.95 * Math.sqrt(Math.max(0, dR * dR - fyR * fyR));
    // Tilt mode always has them; sim mode only with the matching aid on.
    const abs = !sim || this.aids.abs;
    const tc = sim ? this.aids.tc : 2;

    // Tilt: the pedal asks for a share of the available grip. Sim: it asks for brake force.
    const brakeReq = clamp(input.brake, 0, 1) * (sim ? carParams.brakeMaxG.value * m * G : dF + dR);
    const setBias = mods.brakeBias ?? carParams.brakeBias.value;
    const bias = sim
      ? Math.min(0.75, setBias + carParams.brakeByWireOffset.value + carParams.brakeMigration.value * (1 - clamp(this.vx / 70, 0, 1)))
      : setBias;
    const brakeF = abs ? Math.min(brakeReq * bias, spareF) : brakeReq * bias;
    const brakeR = abs ? Math.min(brakeReq * (1 - bias), spareR) : brakeReq * (1 - bias);
    const moving = this.vx > 0.1;
    const brakeTotal = moving ? brakeF + brakeR : 0;
    this.absCut = abs && moving && brakeReq > 0 ? clamp(1 - (brakeF + brakeR) / brakeReq, 0, 1) : 0;

    // The driven wheels' speed (the engine's side of the driveline): road speed plus any wheelspin.
    const wheelSpeed = this.vx + (sim ? this.rearSlip : 0);
    const coupling = sim ? gb.updateCoupling(wheelSpeed, input.clutch ?? 0) : 1;
    // Sim: the pedal is a torque demand through the throttle map.
    const throttle = sim ? Math.pow(clamp(input.throttle, 0, 1), carParams.throttleMap.value) : clamp(input.throttle, 0, 1);
    const power = this.pu.step(
      {
        speed: this.vx,
        // With the clutch fully open the engine just revs; nothing is deployed. The rev limiter cuts both.
        throttle: (sim ? gb.torqueShare : coupling) > 0 && !(sim && gb.limiter) ? throttle : 0,
        brakeForce: brakeTotal,
        overtake: env.overtake,
        deployCapKW: env.deployCapKW,
        iceFactor: sim ? gb.iceFactor() : undefined,
      },
      dt,
    );
    this.mguK = power.mguKW;
    const driveRaw = (power.propulsiveW * carParams.drivetrainEff.value * (sim ? gb.torqueShare : coupling)) / gb.driveSpeed(wheelSpeed);
    const ratio = sim ? gb.overallRatio(gb.gear) : 0;
    const wheelMass = (REAR_WHEELS_INERTIA + ENGINE_INERTIA * ratio * ratio * gb.coupling) / (TYRE_RADIUS * TYRE_RADIUS);
    let drive = tc === 2 ? Math.min(driveRaw, spareR) : driveRaw;
    if (tc === 1) {
      // Slip-limited traction control: lets the rears spin up to a target slip, then trims torque to hold it.
      const { fLong } = this.rearSlipForce(dR, this.vy - b * this.yawRate);
      const target = carParams.tcLowSlip.value * (2 + 0.1 * Math.max(0, this.vx));
      drive = Math.min(driveRaw, (carParams.tcLowSpare.value / 0.95) * spareR, Math.max(0, fLong + (wheelMass * (target - this.rearSlip)) / TC_LOW_TAU));
    }

    this.tcCut = driveRaw > 0 ? clamp(1 - drive / driveRaw, 0, 1) : 0;

    let fxF = moving ? -brakeF : 0;
    let fxR = drive - (moving ? brakeR : 0);
    // Sim: lift-off harvest and engine friction slow the car through the rear tyres.
    // On the brakes, brake-by-wire folds them into the rear brake torque instead.
    const engineBrake = sim && moving && throttle < 0.05 && brakeReq === 0 && gb.gear > 0 ? gb.engineBrakeForce(carParams.engineBrakeNm.value) : 0;
    const regenDrag = power.regenDragW / Math.max(this.vx, 3);
    if (sim && moving) fxR -= Math.min(regenDrag + engineBrake, carParams.engineBrakeMap.value * dR);
    if (sim) {
      ({ fxF, fyF, fxR, fyR } = this.slidingTyres({ dF, dR, fyF, fyR, brakeF, fxR, moving, delta, a, b, dt, wheelMass }));
    } else {
      this.frontLock = this.rearLock = this.rearSpin = 0;
    }

    // Resistances act against forward motion only.
    const resist = moving
      ? dragForce + (carParams.rollingRes.value + env.drag) * m * G + (sim ? 0 : regenDrag)
      : 0;

    const cosD = Math.cos(delta);
    const sinD = Math.sin(delta);
    // Gravity along the slope: uphill slows the car, downhill speeds it up.
    const grade = env.grade ?? 0;
    const gravityAlong = (-m * G * grade) / Math.sqrt(1 + grade * grade);
    const fx = fxR + fxF * cosD - fyF * sinD - resist + gravityAlong;
    const fy = fyF * cosD + fxF * sinD + fyR;

    // Stability assist: pull yaw rate towards the driver's intent once the rear saturates.
    // Target is the steered yaw rate, capped at what the available grip can sustain.
    const rKinematic = (this.vx * Math.tan(delta)) / L;
    const rGrip = (0.95 * (dF + dR)) / m / Math.max(this.vx, 1);
    const rTarget = clamp(rKinematic, -rGrip, rGrip);
    // Like a real stability control, it only removes excess rotation; it never adds yaw.
    const peakSlip = Math.tan(Math.PI / (2 * C)) / B;
    const peakSlipR = Math.tan(Math.PI / (2 * C)) / BR;
    const rearSaturation = smoothstep(0.8 * peakSlipR, 1.4 * peakSlipR, Math.abs(alphaR));
    const yawError = this.yawRate - rTarget;
    const oversteering = Math.sign(yawError) === Math.sign(this.yawRate) || Math.sign(this.yawRate) !== Math.sign(rTarget);
    // Tilt: always on. Sim: the optional, milder stability aid.
    const stabGain = !sim ? carParams.stability.value : this.aids.stability ? carParams.stabilityAid.value : 0;
    const mzStab = oversteering ? -stabGain * Iz * yawError * rearSaturation : 0;
    // Body-slip assist: once the car points too far into (or out of) its direction of
    // travel, yaw it back so a slide recovers instead of settling into a drift.
    const beta = Math.atan2(this.vy, vxs);
    const beta0 = carParams.slipAssistDeg.value * DEG;
    const betaExcess = Math.abs(beta) > beta0 ? beta - Math.sign(beta) * beta0 : 0;
    const mzSlip = sim ? 0 : carParams.slipAssist.value * Iz * betaExcess * kDynFor(this.vx);
    // Off-track drag on one side only (sim): each side carries half the weight, half a track out.
    const mzSide = sim && moving ? ((env.sideDrag ?? 0) * m * G * 0.5 * (TRACK_FRONT + TRACK_REAR)) / 4 : 0;
    const mz = a * (fyF * cosD + fxF * sinD) - b * fyR + mzStab + mzSlip + mzSide;

    this.longAccel = fx / m;
    this.latAccel = fy / m;
    this.vx += (fx / m + this.vy * this.yawRate) * dt;
    this.vy += (fy / m - this.vx * this.yawRate) * dt;
    this.yawRate += (mz / Iz) * dt;

    if (sim) this.axLagged += (this.longAccel - this.axLagged) * (1 - Math.exp(-dt / Math.max(carParams.pitchLag.value, 1e-3)));

    // Near standstill the dynamic model is ill-conditioned; blend to kinematic.
    const kDyn = kDynFor(this.vx);
    const rKin = (this.vx * Math.tan(delta)) / L;
    this.yawRate = rKin + (this.yawRate - rKin) * kDyn;
    this.vy = rKin * b + (this.vy - rKin * b) * kDyn;
    if (this.vx < 0) this.vx = 0;

    this.heading += this.yawRate * dt;
    const s = Math.sin(this.heading);
    const c = Math.cos(this.heading);
    this.x += (this.vx * s + this.vy * c) * dt;
    this.z += (this.vx * c - this.vy * s) * dt;

    this.slipFront = alphaF;
    this.slipRear = alphaR;
    this.gripUseFront = dF > 0 ? Math.hypot(fxF, fyF) / dF : 0;
    this.gripUseRear = dR > 0 ? Math.hypot(fxR, fyR) / dR : 0;

    // Aligning torque: lateral force times total trail. Pneumatic trail shrinks
    // with slip angle (peaking the torque around half the peak slip) and is
    // gone when the tyre slides, so the steering goes light just before the
    // front lets go. Faded in with the dynamic regime: a parked car's tyres
    // scrub rather than self-centre (the FFB adds friction there instead).
    const x = Math.min(1, Math.abs(alphaF) / (carParams.trailFalloff.value * peakSlip));
    const trail = carParams.pneumaticTrail.value * (1 - x) * (1 - x) * (1 - this.frontLock) + carParams.casterTrail.value;
    this.aligningTorque = -fyF * trail * kDynBefore;
    gb.update(this.vx + (sim ? this.rearSlip : 0), dt, throttle);
  }

  /**
   * Sim mode combined slip, lock-ups and wheelspin. A rolling wheel turns brake
   * or drive torque into exactly that much longitudinal force (the wheel finds
   * the slip ratio that does it), and the lateral force gets what the friction
   * ellipse leaves, √(D² − Fx²). As in the brush tyre model, the two barely
   * interact at small slip and the ellipse bites near the limit (a smooth min,
   * softMin). So trail-braking or power mid-corner costs side grip near the
   * limit (entry rotation, power oversteer) instead of locking or spinning the
   * moment the tyre is busy cornering. A wheel only
   * locks or spins up when the torque asks for more than the tyre's peak grip;
   * then it slides, giving sliding grip along its slip velocity, so a locked
   * front stops steering and a spinning rear loses its side grip. Lock and spin
   * ramp in and out over LOCK_TAU so the transitions stay smooth.
   */
  private slidingTyres(t: {
    dF: number;
    dR: number;
    fyF: number;
    fyR: number;
    brakeF: number;
    fxR: number;
    moving: boolean;
    delta: number;
    a: number;
    b: number;
    dt: number;
    /** Rear wheels plus driveline inertia as an equivalent mass at the tyre, kg. */
    wheelMass: number;
  }): { fxF: number; fyF: number; fxR: number; fyR: number } {
    const slide = carParams.slideMu.value;
    const k = Math.min(1, t.dt / LOCK_TAU);
    // Hysteresis: lock past 2% over-demand; release only once the demand drops
    // below what the sliding tyre itself gives (the wheel can spin back up).
    const hold = (current: number, over: boolean, under: boolean): number => (over ? 1 : under ? 0 : current > 0.5 ? 1 : 0);
    /** Rolling tyre: torque sets Fx (up to the peak), the lateral force gets the rest of the ellipse. */
    const ellipse = (fx: number, fy: number, d: number): [number, number] => {
      if (d <= 0) return [0, 0];
      const x = clamp(fx, -d, d);
      return [x, Math.sign(fy) * d * softMin(Math.abs(fy) / d, Math.sqrt(Math.max(0, 1 - (x / d) * (x / d))))];
    };

    // Front: braking only (rear-wheel drive).
    const demandF = t.moving ? -t.brakeF : 0;
    const lockF = hold(this.frontLock, t.moving && t.brakeF > 1.02 * t.dF, !t.moving || t.brakeF < slide * t.dF * 0.85);
    this.frontLock += k * (lockF - this.frontLock);
    let [fxF, fyF] = ellipse(demandF, t.fyF, t.dF);
    if (this.frontLock > 1e-3) {
      // Contact patch velocity in the steered front wheel's frame.
      const px = this.vx;
      const py = this.vy + t.a * this.yawRate;
      const c = Math.cos(t.delta);
      const s = Math.sin(t.delta);
      const vtx = px * c + py * s;
      const vty = -px * s + py * c;
      const v = Math.hypot(vtx, vty) || 1;
      const fs = slide * t.dF;
      fxF += this.frontLock * (-fs * (vtx / v) - fxF);
      fyF += this.frontLock * (-fs * (vty / v) - fyF);
    }

    // Rear: net of drive, braking and engine braking.
    const net = t.fxR;
    const lockR = hold(this.rearLock, t.moving && net < -1.02 * t.dR, !t.moving || -net < slide * t.dR * 0.85);
    this.rearLock += k * (lockR - this.rearLock);
    let [fxR, fyR] = ellipse(net, t.fyR, t.dR);
    const ry = this.vy - t.b * this.yawRate;
    const fs = slide * t.dR;
    if (this.rearLock > 1e-3) {
      const v = Math.hypot(this.vx, ry) || 1;
      fxR += this.rearLock * (-fs * (this.vx / v) - fxR);
      fyR += this.rearLock * (-fs * (ry / v) - fyR);
    }
    // Wheelspin: the rears' slip speed (wheel surface minus road speed) is spun up by
    // drive torque beyond what the tyre gives and pulled back down once it is less,
    // through the inertia of the wheels and the engine in gear. So a small excess
    // lets go progressively and a big one snaps, and easing off recovers it.
    const { fLong, spin, vs } = this.rearSlipForce(t.dR, ry);
    if (net > fLong || this.rearSlip > 0) this.rearSlip = Math.max(0, this.rearSlip + ((net - fLong) / t.wheelMass) * t.dt);
    this.rearSpin = spin;
    if (spin > 1e-3) {
      fxR += spin * (fs * (this.rearSlip / vs) - fxR);
      fyR += spin * (-fs * (ry / vs) - fyR);
    }
    return { fxF, fyF, fxR, fyR };
  }

  /**
   * Rear tyre longitudinal force at the current slip speed: the peak grip while the
   * slip is small, falling to the sliding grip (along the slip velocity) as it grows.
   * `spin` is 0 (gripping) .. 1 (fully spinning).
   */
  private rearSlipForce(dR: number, ry: number): { fLong: number; spin: number; vs: number } {
    const slide = carParams.slideMu.value;
    // Slip speed at which the tyre is fully sliding: past the peak slip ratio (~0.1) plus a margin.
    const uSlide = 2 + 0.1 * Math.max(0, this.vx);
    const spin = smoothstep(0, uSlide, this.rearSlip);
    const vs = Math.hypot(this.rearSlip, ry) || 1;
    return { fLong: dR * (1 - spin) + spin * slide * dR * (this.rearSlip / vs), spin, vs };
  }

  /**
   * Low-speed reversing and rolling backwards (sim mode): kinematic, since the
   * tyre model is only set up for forward travel. Reverse gear tops out at a
   * walking-to-jogging pace, which is all a stuck F1 car needs.
   */
  private stepReverse(input: CarInput, env: CarEnv, delta: number, dt: number): void {
    const gear = this.gearbox.gear;
    const throttle = clamp(input.throttle, 0, 1);
    const push = gear < 0 ? -throttle * 4 : gear > 0 ? throttle * 4 : 0;
    const resist = clamp(input.brake, 0, 1) * 8 + (carParams.rollingRes.value + env.drag) * G + 0.3;
    let vx = this.vx + push * env.grip * dt;
    vx = vx < 0 ? Math.min(0, vx + resist * dt) : vx > 0 && gear < 0 ? Math.max(0, vx - resist * dt) : vx;
    this.vx = Math.max(-8, vx);
    this.vy = 0;
    this.yawRate = (this.vx * Math.tan(delta)) / carParams.wheelbase.value;
    this.longAccel = push;
    this.latAccel = 0;
    this.heading += this.yawRate * dt;
    this.x += this.vx * Math.sin(this.heading) * dt;
    this.z += this.vx * Math.cos(this.heading) * dt;
    this.slipFront = this.slipRear = 0;
    this.gripUseFront = this.gripUseRear = 0;
    this.frontLock = this.rearLock = this.rearSpin = 0;
    this.rearSlip = 0;
    this.aligningTorque = 0;
    this.mguK = 0;
    this.aero.update(false, 0, input.brake, dt);
    this.gearbox.updateCoupling(this.vx, input.clutch ?? 0);
    this.gearbox.update(this.vx, dt, throttle);
  }

  /**
   * Held on the grid (race start lights): the engine runs and revs with the
   * throttle against an open clutch, so a sim-mode launch starts from real rpm.
   */
  holdOnGrid(input: CarInput, dt: number): void {
    if (!this.simMode) return;
    this.gearbox.coupling = 0;
    this.gearbox.update(0, dt, clamp(input.throttle, 0, 1));
  }

  /** Remove velocity pointing along a world-space direction (e.g. into a barrier). */
  removeVelocityAlong(nx: number, nz: number, restitution = 0.2): void {
    const s = Math.sin(this.heading);
    const c = Math.cos(this.heading);
    const wx = this.vx * s + this.vy * c;
    const wz = this.vx * c - this.vy * s;
    const vn = wx * nx + wz * nz;
    if (vn <= 0) return;
    const nwx = wx - (1 + restitution) * vn * nx;
    const nwz = wz - (1 + restitution) * vn * nz;
    // Scrub speed on impact.
    const scrub = 0.85;
    this.vx = Math.max(0, (nwx * s + nwz * c) * scrub);
    this.vy = (nwx * c - nwz * s) * scrub;
    this.yawRate *= 0.5;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** 0 when nearly stopped (kinematic regime), 1 above ~5 m/s. */
function kDynFor(vx: number): number {
  return clamp((vx - 1) / 4, 0, 1);
}

/**
 * Smooth minimum of two fractions in [0, 1] that is exact at either end:
 * softMin(r, 1) = r and softMin(1, a) = a, ≈ min(r, a) elsewhere, rounded where they meet
 * (exponent 4: how sharply the friction ellipse takes over near the limit).
 */
export function softMin(r: number, a: number): number {
  if (r <= 0 || a <= 0) return 0;
  // (r⁻⁴ + a⁻⁴ − 1)^(−1/4), without Math.pow (it runs every physics step).
  const r2 = r * r;
  const a2 = a * a;
  return 1 / Math.sqrt(Math.sqrt(1 / (r2 * r2) + 1 / (a2 * a2) - 1));
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}
